import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { IconButton } from '../button';
import { styled, useStyleConfig, vui } from '../core';
import { T } from '../t';
import { cs, filterUndefined, isString } from '../utils';
import AutoCompletePopover from './autoCompletePopover';
import { clearIconSize, displayValueOnlyTextSize, inputColors, inputStateMapping } from './consts';
import { InputProvider } from './context';
import { getInitialCount } from './helpers';
import HelpText from './helpText';
import InputIcon from './inputIcon';
import InputInput from './inputInput';
export const InputBase = styled.divBox `
  align-items: center;
  color: sandsone.10;
  background-color: white;
  border-radius: none;
  border-width: 1px;
  display: flex;
  flex-shrink: 0;
  outline: none;
  position: relative;
  transition-duration: 0s;
  transform: translate3d(0, 0, 0);
  width: 100%;

  &[aria-disabled='true'] {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: sandstone.79;
    border-color: sandstone.79;
  }
`;
/**
 * Displays an input element wrapped in a div to allow extra content, like counter, side icons or buttons.
 * Forwards many relevant props to the inner input. Handles different states, like loading or error.
 * Exposes some props to the children via context.
 */
export const Input = vui((props, ref) => {
    const { allowClear, autoComplete, autoCompleteOptions, autoCompleteMaxHeight, autoFocus, children, className, defaultValue, disabled, errorText, iconLeft, iconRight, id, input, inputProps, inputRef, itemLeft, itemRight, helpText, max, maxLength, min, name, onBlur, onChange: onChangeProp, onFocus, pattern, placeholder, readOnly, required, showCount, displayValueOnly, size = 'lg', state = '', stateMapping, step, type = 'text', value, variant, ...rest } = props;
    const [count, setCount] = useState(getInitialCount(props));
    const [valueInternal, setValueInternal] = useState(defaultValue || '');
    const states = { ...inputStateMapping, ...stateMapping };
    const styles = useStyleConfig('Input', props);
    const context = useMemo(() => filterUndefined({ disabled, size, variant }), [disabled, size, variant]);
    function onChange(e) {
        setValueInternal(e.target.value);
        setCount(e.target.value.length);
        onChangeProp?.(e);
    }
    function onAutoCompleteSelect(value) {
        const e = { target: { value }, type: 'autoCompleteSelect' };
        onChange(e);
    }
    function onClear() {
        const value = '';
        const e = { target: { value }, type: 'onClear' };
        setValueInternal(value);
        setCount(0);
        onChange(e);
    }
    const aliasedProps = filterUndefined({
        'aria-disabled': disabled,
        bg: readOnly ? 'sandstone.97' : undefined,
        borderColor: readOnly ? 'sandstone.55' : undefined,
        focusWithinBorderColor: !readOnly ? 'transparent' : undefined,
        focusWithinRingColor: readOnly ? 'transparent' : undefined
    });
    useEffect(() => {
        setValueInternal(value ?? '');
        setCount(`${value}`?.length ? `${value}`.length : 0);
    }, [value]);
    const filterAutoCompleteOptions = (i) => valueInternal === i ? false : !valueInternal || i.toLowerCase().includes(`${valueInternal}`.toLowerCase());
    return (_jsxs(InputProvider, { value: context, children: [displayValueOnly ? (_jsx(T, { className: cs('vui-input-value', className), size: displayValueOnlyTextSize[size], children: value || defaultValue })) : (_jsx(AutoCompletePopover, { autoCompleteMaxHeight: autoCompleteMaxHeight, autoCompleteOptions: autoCompleteOptions, filterAutoCompleteOptions: filterAutoCompleteOptions, onAutoCompleteSelect: onAutoCompleteSelect, children: _jsxs(InputBase, { className: cs('vui-input', className), ref: ref, ...styles.container, ...aliasedProps, ...rest, children: [itemLeft, isString(iconLeft) ? _jsx(InputIcon, { ml: 1, name: iconLeft }) : iconLeft, children ?? input ?? (_jsx(InputInput, { ref: inputRef, autoFocus,
                            defaultValue,
                            disabled,
                            id,
                            max,
                            maxLength,
                            min,
                            name,
                            onBlur,
                            onChange,
                            onFocus,
                            pattern,
                            placeholder,
                            readOnly,
                            required,
                            step,
                            type, autoComplete: autoCompleteOptions?.length ? 'off' : autoComplete, value: valueInternal, ...inputProps })), isString(iconRight) ? _jsx(InputIcon, { mr: 1, name: iconRight }) : iconRight, itemRight, state && _jsx(InputIcon, { mr: 1, ...states[state]?.iconProps }), allowClear && (_jsx(IconButton, { disabled: !valueInternal, icon: "falTimes", mr: 0.5, onClick: onClear, size: clearIconSize[size], title: "Clear" })), showCount && (_jsxs(T, { className: "vui-inputCount", color: maxLength && count > maxLength ? inputColors.error : inputColors.helpText, position: "absolute", right: 0, size: "sm", top: "calc(100% + 1px)", children: [count, " ", maxLength ? `/ ${maxLength}` : null] }))] }) })), !!helpText && _jsx(HelpText, { children: helpText }), !!errorText && _jsx(HelpText, { isError: true, children: errorText })] }));
});
Input.Icon = InputIcon;
Input.Input = InputInput;
Input.HelpText = HelpText;
Input.displayName = 'Input';
export default Input;
